import { formatAud, PRODUCT_CONFIG } from "@stb/product-config"
import { Check, type LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

import { useLocale, useLocalHref, useUi } from "@/core/i18n/locale-context"
import type { Ui } from "@/core/i18n/ui"
import { gateCta } from "@/core/launch"
import { Button } from "@/ui/button"

import { RouteBackdrop, ShieldIllustration } from "../home/home-art"
import { AnswerBox } from "./answer-box"
import { Breadcrumb } from "./breadcrumb"
import { CtaBand } from "./cta-band"
import { FaqAccordion } from "./faq-accordion"
import { IconTile } from "./product-art"
import { RelatedLinks } from "./related-links"
import { SimpleTable } from "./simple-table"
import { SourceLine } from "./source-line"

type Source = { label: string; href: string }

/** Plan-table rows that read the billing config; a row is only listed if the page's feature uses it. */
export type PlanRowKey = "trips" | "invoices" | "aiReads" | "vehicles"

const num = (ui: Ui, n: number | null) =>
  n === null ? ui.unlimited : n === 0 ? ui.notIncluded : ui.perMonth(n)

const PLAN_ROWS: Record<PlanRowKey, { header: (ui: Ui) => string; cells: (ui: Ui) => string[] }> = {
  trips: {
    header: (ui) => ui.planRows.trips,
    cells: (ui) => [
      num(ui, PRODUCT_CONFIG.plans.free.tripsMonthly),
      num(ui, PRODUCT_CONFIG.plans.logbook.tripsMonthly),
      num(ui, PRODUCT_CONFIG.plans.pro.tripsMonthly),
    ],
  },
  invoices: {
    header: (ui) => ui.planRows.invoices,
    cells: (ui) => [
      num(ui, PRODUCT_CONFIG.plans.free.invoicesMonthly),
      num(ui, PRODUCT_CONFIG.plans.logbook.invoicesMonthly),
      num(ui, PRODUCT_CONFIG.plans.pro.invoicesMonthly),
    ],
  },
  aiReads: {
    header: (ui) => ui.planRows.aiReads,
    cells: (ui) => [
      num(ui, PRODUCT_CONFIG.plans.free.aiReadsMonthly),
      num(ui, PRODUCT_CONFIG.plans.logbook.aiReadsMonthly),
      num(ui, PRODUCT_CONFIG.plans.pro.aiReadsMonthly),
    ],
  },
  vehicles: {
    header: (ui) => ui.planRows.vehicles,
    cells: (ui) => {
      const extra = ui.eachExtra(formatAud(PRODUCT_CONFIG.addons.vehicle.monthlyCents))
      return [
        String(PRODUCT_CONFIG.plans.free.vehicles),
        `${PRODUCT_CONFIG.plans.logbook.vehicles} (${extra})`,
        `${PRODUCT_CONFIG.plans.pro.vehicles} (${extra})`,
      ]
    },
  },
}

/** Adds an icon to each item by position (icons are presentation, so they live apart from the copy). */
export const withIcons = <T extends object>(items: T[], icons: LucideIcon[]) =>
  items.map((item, i) => ({ ...item, icon: icons[i] }))

/** Everything a product page prints. One file per product under `brand/content/products/`. */
export interface ProductPageContent {
  /** Route pathname, e.g. "/mileage-tracker". */
  path: string
  /** Breadcrumb label for the page. */
  name: string
  /** Small tag above the H1. */
  tag: string
  /** "28 October 2026". */
  reviewed: string
  h1: string
  lead: string
  answer: {
    text: ReactNode
    sources: [Source, ...Source[]]
    financialYear?: string
    checked: string
  }
  cta: { label: string; href: string }
  secondaryCta: { label: string; href: string }
  /** Small print under the hero buttons. */
  heroNote: string
  /** Illustrative card in the hero (decorative; real figures come from the shared fixture). */
  heroCard?: ReactNode
  stats: { value: string; label: string; icon?: LucideIcon }[]
  stepsTitle: string
  /** `art` is a decorative picture under the step text (aria-hidden inside). */
  steps: { title: string; body: string; art?: ReactNode; artOnMobile?: boolean }[]
  example?: {
    title: string
    caption: string
    columns: string[]
    rows: { header: string; cells: string[] }[]
    note: string
  }
  included: { title: string; body: string; icon?: LucideIcon }[]
  limits: { title: string; body: ReactNode; icon?: LucideIcon }
  who: { title: string; body: string; icon?: LucideIcon }[]
  trust?: { title: string; body: string }
  plan: {
    rows: PlanRowKey[]
    extraRows?: { header: string; cells: [string, string, string] }[]
    note?: string
  }
  faq: { question: string; answer: ReactNode }[]
  faqTitle: string
  related: { label: string; href: string }[]
  band: { title: string; description: string }
}

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

const Section = ({ id, title, children }: { id: string; title: string; children: ReactNode }) => (
  <section className="px-band grid gap-4 py-8" aria-labelledby={id}>
    <h2 id={id} className="text-2xl font-semibold">
      {title}
    </h2>
    {children}
  </section>
)

/** Shared template for /mileage-tracker, /receipt-scanner and /invoicing. */
export function ProductPage({ content: c }: { content: ProductPageContent }) {
  const ui = useUi()
  const localHref = useLocalHref()
  const heroCta = gateCta(c.cta, useLocale())
  return (
    <>
      <Breadcrumb items={[{ label: ui.home, href: "/" }, { label: c.name }]} />
      <header className="bg-muted/40 px-band relative isolate grid gap-8 overflow-hidden py-8 md:grid-cols-[1.4fr_1fr] md:items-center md:py-12">
        <RouteBackdrop />
        <div className="grid content-start gap-4">
          <p className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
            <span className="bg-success/15 text-success rounded-full px-3 py-1 font-semibold">
              {c.tag}
            </span>
            <span>
              {ui.lastReviewed} {c.reviewed}
            </span>
          </p>
          <h1 className="text-3xl font-bold tracking-tight md:text-5xl">{c.h1}</h1>
          <p className="text-muted-foreground max-w-[60ch] text-lg">{c.lead}</p>
          <div className="max-w-[70ch]">
            <AnswerBox
              source={
                <SourceLine
                  sources={c.answer.sources}
                  financialYear={c.answer.financialYear}
                  checked={c.answer.checked}
                />
              }
            >
              {c.answer.text}
            </AnswerBox>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="h-11">
              <a href={localHref(heroCta.href)}>{heroCta.label}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-11">
              <a href={localHref(c.secondaryCta.href)}>{c.secondaryCta.label}</a>
            </Button>
          </div>
          <p className="text-muted-foreground text-sm">{c.heroNote}</p>
        </div>
        {c.heroCard && <div className="relative">{c.heroCard}</div>}
      </header>

      <section aria-label={ui.keyFacts} className="px-band py-6">
        <ul className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {c.stats.map(({ value, label, icon: Icon }) => (
            <li
              key={label}
              className="border-border bg-card flex items-center gap-3 rounded-lg border px-4 py-3"
            >
              {Icon && <IconTile icon={Icon} tone="success" />}
              <span>
                <b className="block text-xl tabular-nums">{value}</b>
                <span className="text-muted-foreground text-sm">{label}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <Section id="steps-title" title={c.stepsTitle}>
        <ol className="grid gap-6 md:grid-cols-[repeat(auto-fit,minmax(14rem,1fr))]">
          {c.steps.map(({ title, body, art, artOnMobile }, i) => (
            <li key={title} className="flex gap-4">
              <span
                aria-hidden="true"
                className="bg-primary text-primary-foreground grid size-9 shrink-0 place-items-center rounded-full font-bold"
              >
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold">{title}</h3>
                <p className="text-muted-foreground mt-1">{body}</p>
                {art && <div className={`mt-4 ${artOnMobile ? "" : "hidden md:block"}`}>{art}</div>}
              </div>
            </li>
          ))}
        </ol>
        {c.example && (
          <div className="grid max-w-3xl gap-2">
            <h3 className="font-semibold">{c.example.title}</h3>
            <SimpleTable
              caption={c.example.caption}
              columns={c.example.columns}
              rows={c.example.rows}
            />
            <p className="text-muted-foreground text-sm">{c.example.note}</p>
          </div>
        )}
      </Section>

      <div className="px-band grid gap-8 py-8 md:grid-cols-[1.75fr_1fr] md:items-start">
        <section className="grid gap-4" aria-labelledby="included-title">
          <h2 id="included-title" className="text-2xl font-semibold">
            {ui.whatIsIncluded}
          </h2>
          <ul className="grid gap-4 md:grid-cols-2">
            {c.included.map(({ title, body, icon: Icon }) => (
              <li key={title} className="flex gap-3">
                {Icon ? (
                  <IconTile icon={Icon} />
                ) : (
                  <Check className="text-primary mt-1 size-5 shrink-0" aria-hidden="true" />
                )}
                <div>
                  <b>{title}</b>
                  <p className="text-muted-foreground">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
        <section
          role="note"
          aria-label={ui.whatItDoesNot}
          className="border-border bg-card grid content-start gap-2 rounded-xl border p-5"
        >
          {c.limits.icon && <IconTile icon={c.limits.icon} tone="warning" />}
          <h2 className="text-xl font-semibold">{ui.whatItDoesNot}</h2>
          <p className="font-medium">{c.limits.title}</p>
          <div className="text-muted-foreground">{c.limits.body}</div>
        </section>
      </div>

      <Section id="who-title" title={ui.whoItSuits}>
        <ul className="grid gap-4 md:grid-cols-3">
          {c.who.map(({ title, body, icon: Icon }) => (
            <li
              key={title}
              className="border-border bg-card grid content-start gap-2 rounded-lg border p-5"
            >
              {Icon && <IconTile icon={Icon} />}
              <b>{title}</b>
              <p className="text-muted-foreground">{body}</p>
            </li>
          ))}
        </ul>
      </Section>

      {c.trust && (
        <section
          className="px-band grid items-center gap-8 py-8 md:grid-cols-2"
          aria-labelledby="trust-title"
        >
          <div className="grid gap-3">
            <h2 id="trust-title" className="text-2xl font-semibold">
              {c.trust.title}
            </h2>
            <p className="text-muted-foreground max-w-[70ch]">{c.trust.body}</p>
          </div>
          <div className="hidden justify-self-center md:block">
            <ShieldIllustration />
          </div>
        </section>
      )}

      <section className="px-band grid gap-4 py-8" aria-labelledby="plan-title">
        <div className="flex flex-wrap items-center gap-3">
          <h2 id="plan-title" className="text-2xl font-semibold">
            {ui.whatIsInPlan}
          </h2>
          {PRODUCT_CONFIG.pricesDraft && (
            <span className="bg-accent text-accent-foreground rounded-full px-3 py-1 text-xs font-semibold">
              {ui.draftPrices}
            </span>
          )}
        </div>
        <SimpleTable
          caption={ui.whatIsInPlan}
          columns={["", ui.plans.free, ui.plans.logbook, ui.plans.pro]}
          rows={[
            ...c.plan.rows.map((key) => ({
              header: PLAN_ROWS[key].header(ui),
              cells: PLAN_ROWS[key].cells(ui),
            })),
            ...(c.plan.extraRows ?? []),
          ]}
        />
        {c.plan.note && <p className="text-muted-foreground text-sm">{c.plan.note}</p>}
        <a href={localHref("/pricing")} className={`${linkClass} font-semibold`}>
          {ui.seeFullPricing}
        </a>
      </section>

      <Section id="faq-title" title={c.faqTitle}>
        <FaqAccordion items={c.faq} defaultOpen={0} />
      </Section>

      <RelatedLinks links={c.related} />

      <CtaBand
        title={c.band.title}
        description={c.band.description}
        ctaLabel={c.cta.label}
        ctaHref={c.cta.href}
      />
    </>
  )
}
